import React from 'react';
import {
  Calendar,
  MapPin,
  User,
  Users,
  Clock,
  Eye,
  Edit,
  Trash2,
  Link as LinkIcon
} from 'lucide-react';

const EvenementCard = ({ evenement, onView, onEdit, onDelete }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.slice(0, 5); // HH:MM
  };

  const isUpcoming = () => {
    const eventDate = new Date(evenement.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate >= today;
  };

  const isPast = () => {
    const eventDate = new Date(evenement.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
      {/* Image de couverture */}
      <div className="relative h-48 bg-gradient-to-br from-[#FF6B00] to-[#437011] overflow-hidden">
        {evenement.couverture ? (
          <img
            src={`http://192.168.1.65:8000${evenement.couverture}`}
            alt={evenement.titre}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar size={64} className="text-white opacity-50" />
          </div>
        )}
        
        {/* Badge Statut */}
        <div className="absolute top-3 right-3">
          {isUpcoming() ? (
            <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1">
              <Calendar size={12} />
              À venir
            </span>
          ) : isPast() ? (
            <span className="px-3 py-1 bg-gray-600 text-white text-xs font-bold rounded-full shadow-lg">
              Terminé
            </span>
          ) : (
            <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full shadow-lg">
              Aujourd'hui
            </span>
          )}
        </div>
      </div>

      {/* Contenu */}
      <div className="p-5 space-y-4">
        {/* Titre */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 line-clamp-2 mb-2">
            {evenement.titre}
          </h3>
          {evenement.description && (
            <p className="text-sm text-gray-600 line-clamp-2">
              {evenement.description}
            </p>
          )}
        </div>

        {/* Date et Heure */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Calendar size={16} className="text-[#FF6B00]" />
            <span className="font-medium">{formatDate(evenement.date)}</span>
            {evenement.date_fin && evenement.date_fin !== evenement.date && (
              <span className="text-gray-500">au {formatDate(evenement.date_fin)}</span>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Clock size={16} className="text-[#437011]" />
            <span>{formatTime(evenement.heure_debut)}</span>
            {evenement.heure_fin && (
              <span className="text-gray-500">- {formatTime(evenement.heure_fin)}</span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-700">
            <MapPin size={16} className="text-blue-600" />
            <span className="truncate">{evenement.lieu}</span>
          </div>
        </div>

        {/* Auteur */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
            {evenement.auteur?.first_name?.[0] || evenement.auteur?.username?.[0] || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {evenement.auteur?.first_name && evenement.auteur?.last_name
                ? `${evenement.auteur.first_name} ${evenement.auteur.last_name}`
                : evenement.auteur?.username || 'Auteur'}
            </p>
          </div>
        </div>

        {/* Participants */}
        <div className="bg-purple-50 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-purple-600" />
            <span className="text-sm font-medium text-purple-600">Participants</span>
          </div>
          <p className="text-2xl font-bold text-purple-700 mt-1">{evenement.participants_count || 0}</p>
        </div>

        {/* Lien utile */}
        {evenement.lien_utile && (
          <div className="flex items-center gap-2 text-xs text-blue-600">
            <LinkIcon size={14} />
            <a
              href={evenement.lien_utile}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate hover:underline"
            >
              Lien de l'événement
            </a>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-2">
        <button
          onClick={() => onView(evenement)}
          className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium"
        >
          <Eye size={16} />
          Voir
        </button>
        <button
          onClick={() => onEdit(evenement)}
          className="flex-1 px-3 py-2 bg-gradient-to-r from-[#FF6B00] to-[#e55d00] text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium"
        >
          <Edit size={16} />
          Modifier
        </button>
        <button
          onClick={() => onDelete(evenement)}
          className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
          title="Supprimer"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export default EvenementCard;
